import storageManager from '../services/StorageManager'
import { isCenterpiece, MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import { logger } from '../services/logger'
import {
  MIXED_COLORS,
  MIXED_MATERIALS,
} from '../pages/PhotoStyler/TileDesignerConsts'
import uniqBy from 'lodash/uniqBy'

export const MATERIAL_TYPE_KEY = 'materialType'

export function loadMaterialType() {
  return storageManager.get(MATERIAL_TYPE_KEY)
}

export function saveMaterialType(materialType) {
  if (!materialType) {
    return
  } else if (!validateMaterialType(materialType)) {
    logger.warning('Invalid material type: ', materialType)
    return
  }
  storageManager.set(MATERIAL_TYPE_KEY, materialType)
}

export function validateMaterialType(materialType) {
  if (materialType === MATERIAL_TYPES.STICKER) {
    // Old Jumble material is unsupported
    return false
  }
  return Object.values(MATERIAL_TYPES).includes(materialType)
}

export function isFramedMaterial(materialType) {
  return [MATERIAL_TYPES.CLASSIC, MATERIAL_TYPES.WIDE_FRAME].includes(
    materialType
  )
}

export function getPricingMaterialType(material, actualMaterialsInUse) {
  // We do not support mixed materials in the backend currently so when using mixed materials we will use the discounts
  // of the most expensive material, UNLESS we know the specific materials in use (for same-price bundling)
  if (material === MIXED_MATERIALS) {
    // If we have the actual materials in use, use the first one's material for discount lookup
    // (this happens when all material+frameColor combinations have the same price and should bundle together)
    if (actualMaterialsInUse && actualMaterialsInUse.length > 0) {
      return actualMaterialsInUse[0].material
    }
    // Otherwise, default to most expensive material for mixed materials
    return MATERIAL_TYPES.WIDE_FRAME
  }
  return material
}

/**
 * Extracts unique material and frame color combinations from tiles.
 *
 * @param {Array} tiles - Array of tile objects
 * @returns {Array<{material: MATERIAL_TYPES, frameColor?: FRAME_COLORS}>} - Unique combinations
 */
export function getActualMaterialsInUse(tiles) {
  return uniqBy(
    tiles
      .filter((t) => !isCenterpiece(t))
      .map((t) => ({
        material: t.materialType,
        frameColor: t.customStyle?.frameColor,
      })),
    (item) => `${item.material}-${item.frameColor}`
  )
}

/**
 * Resolves the actual frame color for pricing calculations.
 * For framed materials with mixed colors, uses the first tile's frame color when prices are the same.
 *
 * @param {object} params - The parameters
 * @param {MATERIAL_TYPES} params.backendMaterialType - The resolved backend material type
 * @param {boolean} params.isSinglePriceSingleSize - Whether all items have the same price and size
 * @param {FRAME_COLORS | typeof MIXED_COLORS} params.frameColor - Current frame color (may be MIXED_COLORS)
 * @param {Array|undefined} params.tiles - Array of tile objects
 * @returns {FRAME_COLORS|undefined} - The resolved frame color or undefined for non-framed materials
 */
export function getFrameColorForPricing({
  backendMaterialType,
  isSinglePriceSingleSize,
  frameColor,
  tiles,
}) {
  if (!tiles.length || !isFramedMaterial(backendMaterialType)) {
    return undefined
  }

  if (isSinglePriceSingleSize && frameColor === MIXED_COLORS && tiles) {
    return tiles[0]?.customStyle?.frameColor
  }

  return frameColor
}
